import {
  createActionsColumn,
  createSelectColumn,
} from "@/modules/ui/components/Table";
import { Badge } from "@/modules/shadcn/ui/badge";
import { type EvalLimit } from "@repo/schema";
import { type ColumnDef } from "@tanstack/react-table";
import { PencilIcon, Trash2Icon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { descriptorFor } from "../EvalActions";
import { LabelCell } from "./LabelCell";
import { LimitEnabledSwitch } from "./LimitEnabledSwitch";

interface UseLimitColumnsArgs {
  projectId: number;
  configId: number;
  testCaseId: string;
  onEdit: (limit: EvalLimit) => void;
  onDelete: (limit: EvalLimit) => void;
}

export function useLimitColumns({
  projectId,
  configId,
  testCaseId,
  onEdit,
  onDelete,
}: UseLimitColumnsArgs): ColumnDef<EvalLimit, unknown>[] {
  const { t } = useTranslation(["evaluation", "common"]);
  return useMemo(
    () => [
      createSelectColumn<EvalLimit>(),
      {
        accessorKey: "name",
        header: t("limits.columns.name"),
      },
      {
        // Neither the id nor the header may be "actions": createActionsColumn
        // below already claims that id, and duplicate column ids corrupt the
        // table's per-id sorting and visibility state.
        id: "evalActions",
        header: t("actions.ifNotFulfilled"),
        cell: ({ row }) => {
          const { actions } = row.original;
          if (actions.length === 0) return "-";

          return (
            <div className="flex flex-wrap items-center gap-1">
              {actions.map((action, index) => {
                const descriptor = descriptorFor(action.type);
                if (!descriptor) return null;
                const { Icon, badgeVariant, summarize } = descriptor;
                const label = t(descriptor.label);
                const summary = summarize?.(action, t);

                return (
                  <Badge key={index} variant={badgeVariant} className="gap-1">
                    <Icon />
                    {summary ? t("actions.badge", { label, summary }) : label}
                  </Badge>
                );
              })}
            </div>
          );
        },
      },
      {
        accessorKey: "enabled",
        header: t("limits.columns.status"),
        cell: ({ row }) => (
          <LimitEnabledSwitch
            limit={row.original}
            projectId={projectId}
            configId={configId}
            testCaseId={testCaseId}
          />
        ),
      },
      {
        id: "targetLabel",
        header: t("limits.columns.label"),
        accessorFn: (row: EvalLimit) => row.targetLabel.name,
        cell: ({ row }) => <LabelCell label={row.original.targetLabel} />,
      },
      {
        id: "parentLabel",
        header: t("limits.columns.parent"),
        accessorFn: (row: EvalLimit) => row.targetParentLabel?.name ?? "-",
        cell: ({ row }) =>
          row.original.targetParentLabel ? (
            <LabelCell label={row.original.targetParentLabel} />
          ) : (
            "-"
          ),
      },
      createActionsColumn<EvalLimit>([
        {
          icon: <PencilIcon className="size-4" />,
          onClick: onEdit,
          label: t("limits.editCheck"),
        },
        {
          icon: <Trash2Icon className="size-4 text-destructive" />,
          onClick: onDelete,
          label: t("limits.deleteCheck"),
        },
      ]),
    ],
    [t, projectId, configId, testCaseId, onEdit, onDelete],
  );
}
